import { create } from 'zustand';
import { authService } from '@/services/authService';
import { realtimeService } from '@/services/realtimeService';
import { getFreshAccessToken } from '@/lib/apiClient';
import { onSessionExpired, tokenStorage } from '@/lib/tokenStorage';
import { queryClient } from '@/lib/queryClient';
import { toAppError } from '@/lib/errors';
import { useUIStore } from './useUIStore';
import type { AuthUser, LoginRequest, LoginResponse, LoginResult, Ruolo } from '@/types';
import { isMfaChallenge } from '@/types';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: AuthUser | null;
  status: AuthStatus;
  error: string | null;
  errorCode: string | null;
}

interface AuthActions {
  bootstrap: (options?: { clearOnFailure?: boolean }) => Promise<void>;
  login: (credentials: LoginRequest) => Promise<LoginResult>;
  completeLogin: (session: LoginResponse) => Promise<AuthUser>;
  logout: () => Promise<void>;
  clearError: () => void;
  setUser: (user: AuthUser) => void;
}

/**
 * Session state. Access tokens stay in `tokenStorage` (memory); the refresh
 * token is an HttpOnly cookie. Server data lives in React Query.
 */
export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  user: null,
  status: 'idle',
  error: null,
  errorCode: null,

  bootstrap: async (options) => {
    const clearOnFailure = options?.clearOnFailure !== false;
    if (!tokenStorage.getAccessToken() && !tokenStorage.hasRefreshSession()) {
      set({ status: 'unauthenticated', user: null });
      return;
    }

    set({ status: 'loading' });
    try {
      const token = await getFreshAccessToken();
      if (!token) {
        if (clearOnFailure) tokenStorage.clear();
        set({ user: null, status: 'unauthenticated' });
        return;
      }
      const user = await authService.me();
      set({ user, status: 'authenticated', error: null });
    } catch {
      if (clearOnFailure) tokenStorage.clear();
      set({ user: null, status: 'unauthenticated' });
    }
  },

  login: async (credentials) => {
    set({ error: null, errorCode: null });
    try {
      const result = await authService.login(credentials);
      if (isMfaChallenge(result)) return result;
      set({ user: result.user, status: 'authenticated', error: null, errorCode: null });
      queryClient.clear();
      await realtimeService.reset();
      return result;
    } catch (error) {
      const appError = toAppError(error);
      set({ status: 'unauthenticated', user: null, error: appError.message, errorCode: appError.code ?? null });
      throw error;
    }
  },

  completeLogin: async (session) => {
    tokenStorage.set(session);
    set({ user: session.user, status: 'authenticated', error: null, errorCode: null });
    queryClient.clear();
    await realtimeService.reset();
    return session.user;
  },

  logout: async () => {
    try {
      await authService.logout();
    } finally {
      useUIStore.getState().setImpersonatedClienteId(null);
      set({ user: null, status: 'unauthenticated', error: null, errorCode: null });
      queryClient.clear();
      realtimeService.disconnect();
    }
  },

  clearError: () => set({ error: null, errorCode: null }),

  setUser: (user) => set({ user }),
}));

onSessionExpired(() => {
  const { status } = useAuthStore.getState();
  if (status === 'unauthenticated') return;
  useAuthStore.setState({ user: null, status: 'unauthenticated' });
  useUIStore.getState().setImpersonatedClienteId(null);
  queryClient.clear();
  realtimeService.disconnect();
});

export const selectUser = (state: AuthState & AuthActions): AuthUser | null => state.user;
export const selectIsAuthenticated = (state: AuthState & AuthActions): boolean => state.status === 'authenticated';
export const selectAuthStatus = (state: AuthState & AuthActions): AuthStatus => state.status;
export const selectRole = (state: AuthState & AuthActions): Ruolo | null => state.user?.ruolo ?? null;
